import { useEffect, useState } from 'react'
import { collection, documentId, query, where } from 'firebase/firestore'
import { getDocs, getDocsCacheFirst } from '../lib/firestore'
import { db, isOtaBoot } from '../lib/firebase'
import { classifyError, type ClassifiedError } from '../lib/errors'
import { chunk, FIRESTORE_IN_LIMIT } from '../lib/chunk'
import type { Artist } from '../types'

const EMPTY_RECORD: Record<string, Artist> = {}

const ARTIST_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const TTL_KEY = 'artists:lastFetched'

// Per-artist in-memory cache: survives unmount/remount so returning to a
// previously visited event shows artist data on the first render.
const artistMemCache = new Map<string, Artist>()

function initArtistsFromCache(sortedKey: string): Record<string, Artist> {
  if (!sortedKey) return EMPTY_RECORD
  const result: Record<string, Artist> = {}
  let found = 0
  for (const id of sortedKey.split(',')) {
    const a = artistMemCache.get(id)
    if (a) { result[id] = a; found++ }
  }
  return found > 0 ? result : EMPTY_RECORD
}

export function useArtistsByIds(ids: string[]): {
  artists: Record<string, Artist>
  loading: boolean
  error: ClassifiedError | null
} {
  const sortedKey = [...new Set(ids)].sort().join(',')
  const hasIds = sortedKey.length > 0

  const [storedArtists, setArtists] = useState<Record<string, Artist>>(() => initArtistsFromCache(sortedKey))
  const [storedLoading, setLoading] = useState(true)
  const [error, setError] = useState<ClassifiedError | null>(null)

  const artists = hasIds ? storedArtists : EMPTY_RECORD
  const loading = hasIds ? storedLoading : false

  const [trackedKey, setTrackedKey] = useState(sortedKey)
  if (trackedKey !== sortedKey) {
    setTrackedKey(sortedKey)
    setError(null)
    const newIds = sortedKey.split(',')
    const cached = initArtistsFromCache(sortedKey)
    if (Object.keys(cached).length > 0) {
      setArtists(cached)
      if (!newIds.every(id => cached[id])) setLoading(true)
    } else {
      setLoading(true)
    }
  }

  useEffect(() => {
    if (!hasIds) return

    let cancelled = false
    const uniqueIds = sortedKey.split(',')
    const batches = chunk(uniqueIds, FIRESTORE_IN_LIMIT)

    const lastFetched = Number(localStorage.getItem(TTL_KEY) ?? 0)
    const expired = Date.now() - lastFetched > ARTIST_CACHE_TTL_MS
    // TTL key is only written after a successful network fetch — absent means
    // the artist cache was never populated, so an empty cache result should
    // keep the skeleton instead of rendering "no artists".
    const everFetched = lastFetched > 0

    // Always cache-first so artists render immediately on slow networks
    // instead of blocking on a getDocs network round-trip that may hang.
    Promise.all(
      batches.map(batch =>
        getDocsCacheFirst(query(collection(db, 'artists'), where(documentId(), 'in', batch))),
      ),
    )
      .then(snaps => {
        if (cancelled) return
        const merged: Record<string, Artist> = {}
        snaps.forEach(snap => {
          snap.docs.forEach(d => {
            merged[d.id] = { id: d.id, ...d.data() } as Artist
          })
        })
        Object.entries(merged).forEach(([id, a]) => artistMemCache.set(id, a))
        setArtists(merged)

        const coldCache = Object.keys(merged).length === 0 && !everFetched
        const hasMissing = uniqueIds.some(id => !merged[id])
        // Clear loading immediately when the cache has every requested artist.
        // When artists are missing AND the TTL is fresh, keep loading true until
        // the gap-fill completes — otherwise the schedule renders with blank
        // cards while the gap-fill is in flight.
        if (!coldCache && (!hasMissing || expired)) setLoading(false)

        if (expired) {
          // TTL expired: refresh all from network in the background.
          Promise.all(
            batches.map(batch =>
              getDocs(query(collection(db, 'artists'), where(documentId(), 'in', batch))),
            ),
          )
            .then(freshSnaps => {
              if (cancelled) return
              const freshMerged: Record<string, Artist> = {}
              freshSnaps.forEach(snap => {
                snap.docs.forEach(d => {
                  freshMerged[d.id] = { id: d.id, ...d.data() } as Artist
                })
              })
              Object.entries(freshMerged).forEach(([id, a]) => artistMemCache.set(id, a))
              setArtists(prev => ({ ...prev, ...freshMerged }))
              setLoading(false)
              if (!isOtaBoot) localStorage.setItem(TTL_KEY, String(Date.now()))
            })
            .catch(err => {
              if (cancelled || !coldCache) return
              setError(classifyError(err))
              setLoading(false)
            })
        } else {
          // Fresh TTL: gap-fill any artists missing from cache (newly seeded).
          const missing = uniqueIds.filter(id => !merged[id])
          if (missing.length > 0) {
            Promise.all(
              chunk(missing, FIRESTORE_IN_LIMIT).map(batch =>
                getDocs(query(collection(db, 'artists'), where(documentId(), 'in', batch))),
              ),
            )
              .then(netSnaps => {
                if (cancelled) return
                const gapFilled = { ...merged }
                netSnaps.forEach(snap => {
                  snap.docs.forEach(d => {
                    const a = { id: d.id, ...d.data() } as Artist
                    gapFilled[d.id] = a
                    artistMemCache.set(d.id, a)
                  })
                })
                setArtists(gapFilled)
                setLoading(false)
              })
              .catch(() => { if (!cancelled) setLoading(false) })
          }
        }
      })
      .catch(err => {
        if (cancelled) return
        setError(classifyError(err))
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stable sortedKey, not the ids array identity
  }, [sortedKey])

  return { artists, loading, error }
}
